import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Http, Request, Response, createURLParser, validateUrl } from '@mdd/api';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpUrlEncodingCodec } from './httpUrlEncodingCodec';

export type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';
@Injectable()
export class MddHttpService implements Http {
    // TODO: Server error types
    error = new EventEmitter<Response<any>>();

    constructor(private httpClient: HttpClient) {}

    request<T, V>(request: Request<T>): Promise<Response<V>>;

    request<T, V>(request: Request<T>, responseType: ResponseType): Promise<Response<V>>;

    request<V>(request: Request<any>, responseType: ResponseType): Promise<Response<V>>;

    request<V>(request: Request<any>): Promise<Response<V>>;

    request<T, V>(request: Request<T>, responseType: ResponseType = 'json'): Promise<Response<V>> {
        return new Promise((resolve) => {
            requestAnimationFrame(resolve);
        })
            .then(() => {
                // Validate URL before initialization of the request
                const parser = createURLParser(request.url, environment.apiBase);
                parser.search = request.search || parser.search;
                const finalUrl = parser.href;

                if (
                    !validateUrl(finalUrl, {
                        allowedBaseUrl: environment.apiBase,
                        allowRelativeUrls: true
                    })
                ) {
                    throw this.getBlockedUnsafeUrlError();
                }
            })
            .then(() => {
                return this.httpClient
                    .request(this.toHttpRequest(request, responseType))
                    .pipe(
                        filter((event) => {
                            return event instanceof HttpResponse;
                        })
                    )
                    .toPromise();
            })
            .then((httpResponse: HttpResponse<V>) => {
                return this.toResponse(httpResponse);
            })
            .catch((httpError: HttpErrorResponse) => {
                const response = this.toResponse(httpError);
                this.error.next(response);
                throw response;
            });
    }

    requestPdfExportAsset(url: string): Promise<Response<string>> {
        return new Promise((resolve) => {
            requestAnimationFrame(resolve);
        })
            .then(() => {
                const finalUrl = createURLParser(url).href;

                if (!this.isAllowedPdfExportAssetUrl(finalUrl)) {
                    throw this.getBlockedUnsafeUrlError();
                }

                return this.httpClient
                    .request(this.toHttpRequest({ url: finalUrl }, 'text'))
                    .pipe(
                        filter((event) => {
                            return event instanceof HttpResponse;
                        })
                    )
                    .toPromise();
            })
            .then((httpResponse: HttpResponse<string>) => {
                return this.toResponse(httpResponse);
            })
            .catch((httpError: HttpErrorResponse) => {
                const response = this.toResponse(httpError);
                this.error.next(response);
                throw response;
            });
    }

    private toHttpRequest<T>(
        request: Request<T>,
        responseType: ResponseType = 'json'
    ): HttpRequest<T> {
        // TODO: Find a better way to specify Http implementation-specific options
        if (
            request.url.includes('payment-gateway') ||
            request.url.includes('register') ||
            request.url.includes('change_password')
        ) {
            responseType = 'text';
        }

        const parser = createURLParser(request.url, environment.apiBase);
        return new HttpRequest<T>(request.method || 'GET', parser.href, request.body!, {
            responseType,
            params: new HttpParams({
                fromString: request.search,
                encoder: new HttpUrlEncodingCodec()
            }),
            headers: new HttpHeaders(request.headers),
            withCredentials: request.withCredentials
        });
    }

    private isAllowedPdfExportAssetUrl(url: string): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        try {
            const parser = createURLParser(url);
            const currentOrigin = createURLParser(window.location.href);

            return (
                parser.protocol === currentOrigin.protocol &&
                parser.host === currentOrigin.host &&
                parser.pathname.startsWith('/assets/')
            );
        } catch (error) {
            return false;
        }
    }

    private getBlockedUnsafeUrlError(): HttpErrorResponse {
        return new HttpErrorResponse({
            error: '[SSRF Protection]: Blocked unsafe URL',
            status: 403
        });
    }

    private toResponse<V>(httpResponse: HttpResponse<V> | HttpErrorResponse): Response<V> {
        if (httpResponse instanceof HttpErrorResponse) {
            return {
                body: httpResponse.error,
                status: httpResponse.status
            };
        } else {
            return httpResponse as any;
        }
    }
}
